import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthCtx } from "./AuthProvider";
import { Link } from "react-router-dom";

/* ─── Validation helpers ─── */
const FIO_LATIN_RE = /[a-zA-Z0-9@#$%^&*()\[\]{};:'",./<>?\\|`~!=+_]/;

function normalizePart(s) {
  const t = s.trim().toLowerCase();
  return t ? t[0].toUpperCase() + t.slice(1) : "";
}

function validateFioPart(value, fieldLabel) {
  const trimmed = value.trim();
  if (!trimmed) return `Введите ${fieldLabel}`;
  if (FIO_LATIN_RE.test(trimmed)) return "Только кириллические буквы";
  const parts = trimmed.split("-");
  if (parts.some((p) => !p)) return "Неверное использование дефиса";
  for (const part of parts) {
    if (part.length < 2) return "Минимум 2 буквы";
    if (!/^[А-ЯЁа-яё]/.test(part)) return "Только кириллические буквы";
  }
  return null;
}

function maskEmail(email) {
  const [user, domain] = email.split("@");
  if (!domain) return email;
  const masked = user[0] + "***" + (user.length > 4 ? user[user.length - 1] : "");
  return `${masked}@${domain}`;
}

/* ─── Floating-label input ─── */
function FloatingInput({ label, value, onChange, onBlur, onFocus, type = "text", id, autoComplete, required, optional = false, error = false, children }) {
  const [focused, setFocused] = useState(false);
  const floated = focused || Boolean(value);
  const isPassword = type === "password";
  const borderColor = focused ? "#3b82f6" : error ? "#f87171" : "#e2e8f0";

  // type="email" blocks selectionStart — use text internally
  const inputType = type === "email" ? "text" : type;

  const uidRef     = useRef(0);
  const charsRef   = useRef([]);
  const prevLenRef = useRef(0);
  const [chars, setChars] = useState([]);
  const [cursorPos, setCursorPos] = useState(0);
  const inputRef = useRef(null);

  const syncCursor = (e) => setCursorPos(e.target.selectionStart ?? value.length);

  useEffect(() => {
    const display = isPassword ? "•".repeat(value.length) : value;
    const prev    = charsRef.current;
    const adding  = display.length === prevLenRef.current + 1;
    const next = display.split("").map((ch, i) => {
      if (i < prev.length && prev[i].ch === ch) return prev[i];
      return { ch, uid: ++uidRef.current, fresh: adding && i >= prevLenRef.current };
    });
    charsRef.current   = next;
    prevLenRef.current = display.length;
    setChars(next);
    if (adding) {
      const t = setTimeout(() => {
        charsRef.current = charsRef.current.map(c => ({ ...c, fresh: false }));
        setChars([...charsRef.current]);
      }, 280);
      return () => clearTimeout(t);
    }
  }, [value, isPassword]);

  return (
    <div className="relative mt-2">

      {/* Notched fieldset border */}
      <fieldset
        className="absolute inset-0 rounded-2xl pointer-events-none m-0 p-0 transition-colors duration-200"
        style={{ border: `1px ${optional ? "dashed" : "solid"} ${borderColor}`, padding: 0, margin: 0 }}
      >
        <legend style={{
          marginLeft: 12, padding: "0 4px", height: 0, overflow: "hidden",
          display: "block", whiteSpace: "nowrap", visibility: "hidden",
          fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
          maxWidth: floated ? 999 : 0, overflow: "hidden", transition: "max-width 200ms ease",
        }}>
          {` ${label} `}
        </legend>
      </fieldset>

      {/* Transparent real input — handles focus, keyboard, autocomplete, a11y */}
      <input
        ref={inputRef}
        id={id}
        type={inputType}
        value={value}
        onChange={(e) => { onChange(e); syncCursor(e); }}
        onFocus={(e) => { setFocused(true); syncCursor(e); onFocus?.(e); }}
        onBlur={(e) => { setFocused(false); onBlur?.(e); }}
        onClick={syncCursor}
        onKeyUp={syncCursor}
        onSelect={syncCursor}
        autoComplete={autoComplete}
        required={required}
        placeholder=""
        className="absolute inset-0 w-full h-full opacity-0 z-20 cursor-text rounded-2xl"
        style={{ caretColor: "transparent" }}
      />

      {/* Visual layer with animated character spans + blinking caret */}
      <div className={`w-full h-14 ${children ? "pr-14" : "pr-4"} pl-4 flex items-center rounded-2xl bg-white dark:bg-transparent pointer-events-none select-none overflow-hidden transition-all duration-200`}>
        <div
          className="flex items-center text-slate-900 dark:text-slate-100 text-base leading-none"
          style={{ marginTop: floated ? 10 : 0, transition: "margin-top 200ms ease" }}
        >
          {focused && cursorPos === 0 && (
            <span className="caret-blink" style={{ display: "inline-block", width: 1.5, minWidth: 1.5, height: "1.15em", background: "#3b82f6", marginLeft: 0, verticalAlign: "middle", borderRadius: 1 }} />
          )}
          {chars.map(({ ch, uid, fresh }, i) => (
            <React.Fragment key={uid}>
              <span className={fresh ? "char-in" : ""} style={{ display: "inline-block" }}>
                {ch === " " ? " " : ch}
              </span>
              {focused && cursorPos === i + 1 && (
                <span className="caret-blink" style={{ display: "inline-block", width: 1.5, minWidth: 1.5, height: "1.15em", background: "#3b82f6", marginLeft: 1, verticalAlign: "middle", borderRadius: 1 }} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Floating label */}
      <label
        htmlFor={id}
        className={`absolute pointer-events-none select-none transition-all duration-200 z-10 ${
          floated
            ? `top-0 -translate-y-1/2 text-[11px] font-bold uppercase tracking-widest px-1 bg-transparent ${focused ? "text-blue-500 dark:text-blue-400" : error ? "text-red-400" : "text-slate-400 dark:text-slate-500"}`
            : "top-1/2 -translate-y-1/2 text-sm leading-none text-slate-400 dark:text-slate-500"
        }`}
        style={{ left: 12 }}
      >
        {label}
      </label>

      {children}
    </div>
  );
}

/* ─── Monkey password toggle ─── */
const MonkeyToggle = ({ shown, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={shown ? "Скрыть пароль" : "Показать пароль"}
    aria-pressed={shown}
    className="absolute right-3 top-1/2 -translate-y-1/2 z-30 h-10 w-10 flex items-center justify-center rounded-full outline-none transition-transform active:scale-90"
  >
    <span className="relative block h-8 w-8 opacity-80 hover:opacity-100 transition-opacity">
      <style>{`
        @keyframes arm-l-in{0%{transform:translate(-10px,11px) rotate(-50deg) scale(.88);opacity:0}100%{transform:translate(0,0) rotate(0deg) scale(1);opacity:1}}
        @keyframes arm-r-in{0%{transform:translate(10px,11px) rotate(50deg) scale(.88);opacity:0}100%{transform:translate(0,0) rotate(0deg) scale(1);opacity:1}}
        @keyframes arm-l-out{0%{transform:translate(0,0) rotate(0deg) scale(1);opacity:1}100%{transform:translate(-10px,11px) rotate(-50deg) scale(.88);opacity:0}}
        @keyframes arm-r-out{0%{transform:translate(0,0) rotate(0deg) scale(1);opacity:1}100%{transform:translate(10px,11px) rotate(50deg) scale(.88);opacity:0}}
      `}</style>
      <svg viewBox="0 0 64 64" className="h-8 w-8">
        <ellipse cx="32" cy="34" rx="20" ry="18" fill="#9a613d" />
        <ellipse cx="32" cy="36.5" rx="12.5" ry="10.5" fill="#e0b494" />
        <circle cx="14" cy="22" r="8.1" fill="#8b5637" /><circle cx="50" cy="22" r="8.1" fill="#8b5637" />
        <circle cx="14" cy="22" r="4.5" fill="#dca88a" /><circle cx="50" cy="22" r="4.5" fill="#dca88a" />
        <ellipse cx="26" cy="31" rx="2.8" ry={shown ? 3.1 : 1.1} fill="#1f2937" style={{ transition: "all 460ms ease" }} />
        <ellipse cx="38" cy="31" rx="2.8" ry={shown ? 3.1 : 1.1} fill="#1f2937" style={{ transition: "all 460ms ease" }} />
        <ellipse cx="32" cy="38.9" rx="2.1" ry="1.5" fill="#7c4f32" />
        <path d="M27.5 43c2.1 2 6.9 2 9 0" stroke="#6b4228" strokeWidth="1.8" fill="none" strokeLinecap="round" />
      </svg>
      <span className="absolute left-[-8px] top-[10px]" style={{ opacity: shown ? 0 : 1, animation: shown ? "arm-l-out 520ms ease forwards" : "arm-l-in 520ms ease forwards" }}>
        <svg viewBox="0 0 24 28" className="h-[28px] w-[24px]"><path d="M17.2 5.6 C11 7.2, 5 12.5, 4.2 18.4 C3.8 21.6, 6.2 24, 9.6 24 C12.2 24, 14.2 23, 15.7 21.2" stroke="#c18457" strokeWidth="1" fill="none" /><circle cx="18" cy="4.8" r="5.4" fill="#c18457" /><circle cx="18" cy="4.8" r="3.3" fill="#e4b796" /></svg>
      </span>
      <span className="absolute right-[-8px] top-[10px]" style={{ opacity: shown ? 0 : 1, animation: shown ? "arm-r-out 520ms ease forwards" : "arm-r-in 520ms ease forwards" }}>
        <svg viewBox="0 0 24 28" className="h-[28px] w-[24px]"><path d="M6.8 5.6 C13 7.2, 19 12.5, 19.8 18.4 C20.2 21.6, 17.8 24, 14.4 24 C11.8 24, 9.8 23, 8.3 21.2" stroke="#c18457" strokeWidth="1" fill="none" /><circle cx="6" cy="4.8" r="5.4" fill="#c18457" /><circle cx="6" cy="4.8" r="3.3" fill="#e4b796" /></svg>
      </span>
    </span>
  </button>
);

/* ─── Global animations ─── */
const BG_STYLE = `
  @keyframes card-enter {
    from { opacity:0; transform: translateY(24px) scale(0.97); }
    to   { opacity:1; transform: translateY(0) scale(1); }
  }
  @keyframes soft-shake {
    0%,100% { transform:translateX(0); }
    15%  { transform:translateX(-8px); }
    30%  { transform:translateX(6px); }
    45%  { transform:translateX(-4px); }
    60%  { transform:translateX(2px); }
    75%  { transform:translateX(-1px); }
  }
  @keyframes code-pop {
    0%  { transform: scale(0.8); opacity: 0; }
    70% { transform: scale(1.05); }
    100%{ transform: scale(1);   opacity: 1; }
  }
  @keyframes mode-in {
    from { opacity: 0; transform: translateY(7px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes char-in {
    from { opacity: 0; transform: translateY(-6px) scale(0.6); }
    to   { opacity: 1; transform: translateY(0)    scale(1);   }
  }
  @keyframes caret-blink {
    0%, 100% { opacity: 1; }
    50%      { opacity: 0; }
  }
  .card-enter  { animation: card-enter  0.45s cubic-bezier(0.34,1.4,0.64,1) forwards; }
  .code-pop    { animation: code-pop   0.35s cubic-bezier(0.34,1.56,0.64,1) forwards; }
  .mode-in     { animation: mode-in    0.28s cubic-bezier(0.2,0.9,0.2,1) forwards; }
  .char-in     { animation: char-in    220ms cubic-bezier(0.34,1.56,0.64,1) both; }
  .caret-blink { animation: caret-blink 1s step-end infinite; }
  .animate-soft-shake { animation: soft-shake 0.6s cubic-bezier(.36,.07,.19,.97) both; }

  @keyframes fio-split {
    0%   { box-shadow: 0 0 0 0 rgba(59,130,246,0); }
    40%  { box-shadow: 0 0 0 12px rgba(59,130,246,0.13); }
    100% { box-shadow: 0 0 0 0 rgba(59,130,246,0); }
  }
  .fio-split { animation: fio-split 0.55s cubic-bezier(0.2,0.9,0.2,1) forwards; }

  @media (prefers-reduced-motion: reduce) {
    .card-enter, .code-pop, .mode-in, .char-in, .animate-soft-shake {
      animation: none !important;
      opacity: 1 !important;
      transform: none !important;
    }
    .caret-blink { animation: none !important; opacity: 1 !important; }
  }
`;

function AnimatedBg() {
  return <style>{BG_STYLE}</style>;
}

/* ─── Smooth expand/collapse via CSS grid ─── */
function SlideSection({ show, children }) {
  return (
    <div style={{
      display: "grid",
      gridTemplateRows: show ? "1fr" : "0fr",
      opacity: show ? 1 : 0,
      transition: "grid-template-rows 320ms cubic-bezier(0.2,0.9,0.2,1), opacity 240ms cubic-bezier(0.2,0.9,0.2,1)",
    }}>
      <div style={{ overflow: "hidden", minHeight: 0 }}>
        {children}
      </div>
    </div>
  );
}

/* ─── Main card wrapper ─── */
function Card({ children, shake }) {
  return (
    <div className="card-enter w-full max-w-[420px]">
      <div className={`bg-white dark:bg-[#2a2c32] border border-slate-200/80 dark:border-slate-700/40 shadow-[0_15px_40px_rgba(0,0,0,0.06)] dark:shadow-[0_15px_40px_rgba(0,0,0,0.4)] rounded-[2.5rem] px-8 py-10 md:px-10 ${shake ? "animate-soft-shake" : ""}`}>
        {children}
      </div>
    </div>
  );
}

/* ─── Submit button ─── */
function SubmitButton({ busy, children }) {
  return (
    <button
      disabled={busy}
      className="w-full h-14 rounded-2xl bg-[#3b82f6] hover:bg-[#1e3a8a] text-white font-bold text-[16px] active:scale-[0.97] transition-all duration-200 disabled:opacity-50 flex items-center justify-center"
    >
      {busy
        ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        : children}
    </button>
  );
}

/* ═══════════════════════════════════════════ */
export default function Login() {
  const { login, register, registerStudent, verifyEmail, resendVerification } = useContext(AuthCtx);
  const [searchParams, setSearchParams] = useSearchParams();

  const [mode, setMode] = useState("login");
  const [isTeacherInviteMode, setIsTeacherInviteMode] = useState(false);

  const [rememberMe, setRememberMe] = useState(false);
  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [patronymic, setPatronymic] = useState("");
  const [fioExpanded, setFioExpanded] = useState(false);
  const [fioJustExpanded, setFioJustExpanded] = useState(false);
  const [lastNameTouched, setLastNameTouched] = useState(false);
  const [firstNameTouched, setFirstNameTouched] = useState(false);
  const [patronymicTouched, setPatronymicTouched] = useState(false);
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [verifyEmailAddr, setVerifyEmailAddr] = useState("");
  const [resentNotice, setResentNotice] = useState(false);
  const [codeDigits, setCodeDigits] = useState(["", "", "", "", "", ""]);
  const [resendCooldown, setResendCooldown] = useState(0);
  const codeRefs = useRef([]);
  const fioGroupRef = useRef(null);
  const fioBlurTimerRef = useRef(null);
  const submittingRef = useRef(false);

  const [tgLink, setTgLink] = useState(null);

  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const m = (searchParams.get("m") || searchParams.get("mode") || "").toLowerCase();
    const i = (searchParams.get("i") || searchParams.get("invite") || "").trim();
    if ((m === "teacher-register" || m === "tr") && i) {
      setMode("register"); setIsTeacherInviteMode(true); setInviteCode(i);
    } else if (m === "register") {
      setMode("register"); setIsTeacherInviteMode(false);
    } else {
      setMode("login"); setIsTeacherInviteMode(false);
    }
  }, [searchParams]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((v) => v - 1), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  useEffect(() => {
    if (mode !== "tg_verify" || !verifyEmailAddr) return;
    let active = true;
    const check = async () => {
      try {
        const res = await fetch(`/auth/tg-verify-status?email=${encodeURIComponent(verifyEmailAddr)}`);
        if (!res.ok || !active) return;
        const data = await res.json();
        if (data.verified && active) {
          active = false;
          sessionStorage.setItem("token", data.access_token);
          if (data.refresh_token) sessionStorage.setItem("refresh_token", data.refresh_token);
          navigate("/", { replace: true });
          window.location.reload();
        }
      } catch (_) {}
    };
    check();
    const id = setInterval(check, 2000);
    return () => { active = false; clearInterval(id); };
  }, [mode, verifyEmailAddr, navigate]);

  useEffect(() => {
    if (mode !== "register") setFioExpanded(false);
  }, [mode]);

  const triggerError = (msg) => {
    setErr(msg); setIsShaking(true);
    setTimeout(() => setIsShaking(false), 600);
  };

  const startResendCooldown = () => setResendCooldown(60);

  const composedFio = [lastName, firstName, patronymic].filter(Boolean).join(" ");

  const handleFioFocus = () => {
    clearTimeout(fioBlurTimerRef.current);
    if (!fioExpanded) {
      setFioExpanded(true);
      setFioJustExpanded(true);
      setTimeout(() => setFioJustExpanded(false), 600);
    }
  };

  const handleFioBlur = () => {
    fioBlurTimerRef.current = setTimeout(() => {
      if (fioGroupRef.current && !fioGroupRef.current.contains(document.activeElement)) {
        setFioExpanded(false);
      }
    }, 0);
  };

  const handleCodeInput = (idx, value) => {
    const clean = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (clean.length > 1) {
      const next = ["", "", "", "", "", ""];
      for (let i = 0; i < Math.min(clean.length, 6); i++) next[i] = clean[i];
      setCodeDigits(next);
      codeRefs.current[Math.min(clean.length, 5)]?.focus();
      return;
    }
    const ch = clean.slice(-1);
    const next = [...codeDigits]; next[idx] = ch; setCodeDigits(next);
    if (ch && idx < 5) codeRefs.current[idx + 1]?.focus();
  };
  const handleCodeKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !codeDigits[idx] && idx > 0)
      codeRefs.current[idx - 1]?.focus();
  };
  const handleCodePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData?.getData("text") || "";
    const pasted = text.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
    if (!pasted) return;
    const next = ["", "", "", "", "", ""];
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setCodeDigits(next);
    codeRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || busy) return;
    try {
      setBusy(true);
      await resendVerification(verifyEmailAddr);
      startResendCooldown(); setErr("");
    } catch (e) { triggerError(e.message || "Не удалось отправить код"); }
    finally { setBusy(false); }
  };

  const enterVerifyMode = (emailAddr, resent = false) => {
    setVerifyEmailAddr(emailAddr);
    setCodeDigits(["", "", "", "", "", ""]);
    setResentNotice(resent);
    setMode("verify");
    startResendCooldown();
    setTimeout(() => codeRefs.current[0]?.focus(), 120);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setErr(""); setBusy(true);
    try {
      if (mode === "verify") {
        const code = codeDigits.join("");
        if (code.length < 6) return void triggerError("Введите полный 6-значный код");
        await verifyEmail(verifyEmailAddr, code);
        navigate("/", { replace: true }); window.location.reload(); return;
      }
      if (mode === "register") {
        const lnErr = validateFioPart(lastName, "фамилию");
        const fnErr = validateFioPart(firstName, "имя");
        if (lnErr) return void triggerError(lnErr);
        if (fnErr) return void triggerError(fnErr);
        if (patronymic.trim()) {
          const pErr = validateFioPart(patronymic, "отчество");
          if (pErr) return void triggerError(pErr);
        }
        const fioFinal = [normalizePart(lastName), normalizePart(firstName), patronymic.trim() ? normalizePart(patronymic) : ""].filter(Boolean).join(" ");
        const result = await (isTeacherInviteMode
          ? register(fioFinal, email, pass, inviteCode.trim())
          : registerStudent(fioFinal, email, pass));
        if (result?.needsVerification) {
          if (result.tgLink) {
            setTgLink(result.tgLink);
            setVerifyEmailAddr(result.email || email);
            setMode("tg_verify");
            return;
          }
          enterVerifyMode(result.email || email, result.resent);
          return;
        }
        navigate("/", { replace: true }); window.location.reload(); return;
      }
      if (pass.length < 6) return void triggerError("Введите пароль");
      try {
        await login(email, pass, rememberMe);
      } catch (loginErr) {
        if (loginErr.message === "email_not_verified") {
          try { await resendVerification(email); } catch (_) { /* ignore */ }
          enterVerifyMode(email); return;
        }
        throw loginErr;
      }
      navigate("/", { replace: true }); window.location.reload();
    } catch (e2) { triggerError(e2?.message || "Ошибка: проверьте данные"); }
    finally { setBusy(false); submittingRef.current = false; }
  };

  /* ──── TG VERIFY SCREEN ──── */
  if (mode === "tg_verify") {
    return (
      <div className="h-[100dvh] flex items-center justify-center px-4 py-4 overflow-y-auto overscroll-contain">
        <AnimatedBg />
        <Card shake={isShaking}>
          <div className="flex justify-center mb-6">
            <div className="h-14 w-14 rounded-2xl flex items-center justify-center shadow-[0_8px_20px_-8px_rgba(41,182,246,0.4)]" style={{background:"#29b6f6"}}>
              <svg className="h-7 w-7 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
              </svg>
            </div>
          </div>

          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 text-center mb-1">Подтвердите через Telegram</h1>
          <p className="text-slate-400 dark:text-slate-500 text-[13px] text-center mb-6 mt-2">
            Откройте бота и нажмите <span className="font-semibold text-slate-600 dark:text-slate-300">Start</span>.
            Страница войдёт автоматически.
          </p>

          <a
            href={tgLink}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center py-3 px-4 rounded-2xl text-white font-bold text-[15px] transition-colors shadow-sm mb-5"
            style={{background:"#29b6f6"}}
            onMouseOver={e => e.currentTarget.style.background="#039be5"}
            onMouseOut={e => e.currentTarget.style.background="#29b6f6"}
          >
            Открыть Telegram
          </a>

          <div className="flex items-center justify-center gap-2 text-slate-400 dark:text-slate-500 text-[13px]">
            <svg className="animate-spin h-4 w-4 text-blue-400 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
            Ожидание подтверждения...
          </div>

          <div className="text-center mt-6">
            <button
              type="button"
              onClick={() => { setMode("login"); setSearchParams({}); }}
              className="text-[13px] text-slate-400 hover:text-slate-600 transition-colors"
            >
              ← Вернуться ко входу
            </button>
          </div>
        </Card>
      </div>
    );
  }

  /* ──── VERIFY SCREEN ──── */
  if (mode === "verify") {
    return (
      <div className="h-[100dvh] flex items-center justify-center px-4 py-4 overflow-y-auto overscroll-contain">
        <AnimatedBg />
        <Card shake={isShaking}>
          <div className="flex justify-center mb-6">
            <div className="h-14 w-14 rounded-2xl bg-[#3b82f6] flex items-center justify-center shadow-[0_8px_20px_-8px_rgba(59,130,246,0.4)]">
              <svg className="h-7 w-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
          </div>

          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 text-center mb-1">Подтвердите почту</h1>
          {resentNotice && (
            <div className="mb-4 mt-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 px-3 py-2 text-[12.5px] text-amber-700 dark:text-amber-300 text-center">
              Аккаунт с этой почтой уже существует. Код подтверждения отправлен повторно.
            </div>
          )}
          <p className="text-slate-400 dark:text-slate-500 text-[13px] text-center mb-8">
            Код отправлен на <span className="font-semibold text-slate-600 dark:text-slate-300">{maskEmail(verifyEmailAddr)}</span>
          </p>

          <form onSubmit={submit} className="space-y-6">
            <div className="flex gap-2 justify-center" role="group" aria-label="Код подтверждения">
              {codeDigits.map((digit, idx) => (
                <input
                  key={idx}
                  ref={(el) => { codeRefs.current[idx] = el; }}
                  type="text"
                  inputMode="text"
                  maxLength={6}
                  value={digit}
                  aria-label={`Символ ${idx + 1} из 6`}
                  onChange={(e) => handleCodeInput(idx, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(idx, e)}
                  onPaste={handleCodePaste}
                  className={`code-pop w-12 h-14 text-center text-xl font-mono font-bold border-2 rounded-2xl outline-none uppercase bg-white dark:bg-[#222428] text-slate-900 dark:text-slate-100 transition-all active:scale-[0.96] ${
                    digit ? "border-blue-400 dark:border-blue-500/70 bg-blue-50 dark:bg-[rgba(30,58,138,0.3)] text-blue-700 dark:text-blue-300 shadow-sm" : "border-slate-200 dark:border-slate-700/60 hover:border-slate-300 dark:hover:border-slate-600"
                  } focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:focus:ring-blue-400/15`}
                  style={{ animationDelay: `${idx * 50}ms`, animationFillMode: "both" }}
                />
              ))}
            </div>

            <p role="alert" aria-live="polite" className="text-red-500 text-[13px] font-semibold text-center min-h-[1.25rem]">{err}</p>

            <SubmitButton busy={busy}>Подтвердить</SubmitButton>

            <div className="text-center space-y-2 pt-1">
              <button
                type="button"
                disabled={resendCooldown > 0 || busy}
                onClick={handleResend}
                className="text-[13px] font-semibold text-blue-600 hover:text-blue-800 transition-colors disabled:text-slate-400 disabled:cursor-not-allowed"
              >
                {resendCooldown > 0 ? `Повторить через ${resendCooldown} с` : "Не получили код? Отправить повторно"}
              </button>
              <div>
                <button
                  type="button"
                  onClick={() => { setMode("login"); setSearchParams({}); }}
                  className="text-[13px] text-slate-400 hover:text-slate-600 transition-colors"
                >
                  ← Вернуться ко входу
                </button>
              </div>
            </div>
          </form>
        </Card>
      </div>
    );
  }

  /* ──── LOGIN / REGISTER SCREEN ──── */
  const isRegister = mode === "register";
  const title = isRegister ? (isTeacherInviteMode ? "Преподаватель" : "Регистрация") : "Добро пожаловать";

  return (
    <div className="h-[100dvh] flex items-center justify-center px-4 py-4 overflow-y-auto overscroll-contain">
      <AnimatedBg />
      <Card shake={isShaking}>
        <h1 key={title} className="mode-in text-[26px] font-bold text-slate-900 dark:text-slate-100 text-center mb-7 tracking-tight">{title}</h1>

        <form onSubmit={submit} className="space-y-4">
          <SlideSection show={isRegister}>
            <div className="pb-1">
              <div ref={fioGroupRef}>
                <div className={fioJustExpanded ? "fio-split rounded-2xl" : ""}>
                  <FloatingInput
                    id="lastName"
                    label={fioExpanded ? "Фамилия" : "ФИО"}
                    value={fioExpanded ? lastName : composedFio}
                    onChange={(e) => { if (fioExpanded) setLastName(e.target.value); }}
                    onFocus={handleFioFocus}
                    onBlur={() => { setLastNameTouched(true); handleFioBlur(); }}
                    error={Boolean(fioExpanded && lastNameTouched && validateFioPart(lastName, "фамилию"))}
                    required={isRegister}
                  />
                </div>
                {fioExpanded && lastNameTouched && validateFioPart(lastName, "фамилию") && (
                  <p className="text-red-500 text-[12px] mt-1 ml-1">{validateFioPart(lastName, "фамилию")}</p>
                )}
                <SlideSection show={fioExpanded}>
                  <div className="pt-3 flex gap-3">
                    <div className="flex-1 min-w-0">
                      <FloatingInput
                        id="firstName"
                        label="Имя"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        onFocus={handleFioFocus}
                        onBlur={() => { setFirstNameTouched(true); handleFioBlur(); }}
                        error={Boolean(firstNameTouched && validateFioPart(firstName, "имя"))}
                        required={isRegister}
                      />
                      {firstNameTouched && validateFioPart(firstName, "имя") && (
                        <p className="text-red-500 text-[12px] mt-1 ml-1">{validateFioPart(firstName, "имя")}</p>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <FloatingInput
                        id="patronymic"
                        label="Отчество"
                        optional
                        value={patronymic}
                        onChange={(e) => setPatronymic(e.target.value)}
                        onFocus={handleFioFocus}
                        onBlur={() => { setPatronymicTouched(true); handleFioBlur(); }}
                        error={Boolean(patronymicTouched && patronymic.trim() && validateFioPart(patronymic, "отчество"))}
                      />
                      {patronymicTouched && patronymic.trim() && validateFioPart(patronymic, "отчество") && (
                        <p className="text-red-500 text-[12px] mt-1 ml-1">{validateFioPart(patronymic, "отчество")}</p>
                      )}
                    </div>
                  </div>
                </SlideSection>
              </div>
              {isTeacherInviteMode && (
                <div className="mt-3 flex items-center gap-2 px-4 py-3 rounded-2xl bg-blue-50/80 border border-blue-100">
                  <svg className="h-4 w-4 text-blue-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  <span className="text-blue-700 font-mono text-sm flex-1 truncate">{inviteCode}</span>
                  <span className="text-[10px] text-blue-400 font-semibold uppercase tracking-wide">Инвайт</span>
                </div>
              )}
            </div>
          </SlideSection>

          <FloatingInput
            id="email"
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value.toLowerCase().trim())}
            autoComplete="email"
            required
          />

          <div>
            <FloatingInput
              id="password"
              label="Пароль"
              type={showPassword ? "text" : "password"}
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              autoComplete={isRegister ? "new-password" : "current-password"}
              required
            >
              <MonkeyToggle shown={showPassword} onClick={() => setShowPassword((v) => !v)} />
            </FloatingInput>

            <SlideSection show={isRegister && pass.length > 0}>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 px-1 pt-2 pb-0.5">
                {[
                  { label: "8+ символов",        ok: pass.length >= 8 },
                  { label: "Прописная A–Z",       ok: /[A-Z]/.test(pass) },
                  { label: "Строчная a–z",        ok: /[a-z]/.test(pass) },
                  { label: "Цифра 0–9",           ok: /\d/.test(pass) },
                  { label: "Спецсимвол !@#…",     ok: /[!@#$%^&*()\-_=+[\]{}|;:'",.<>?\/\\`~]/.test(pass) },
                  { label: "Не 3+ одинаковых",   ok: !/(.)\1{2,}/.test(pass) },
                ].map(({ label, ok }) => (
                  <span key={label} className={`text-[12px] flex items-center gap-1 transition-colors ${ok ? "text-emerald-600" : "text-slate-400"}`}>
                    <span>{ok ? "✓" : "○"}</span> {label}
                  </span>
                ))}
              </div>
            </SlideSection>

            <SlideSection show={!isRegister}>
              <div className="flex items-center justify-between pt-3 px-1">
                <button
                  type="button"
                  onClick={() => setRememberMe((v) => !v)}
                  className="flex items-center gap-2.5 group"
                >
                  <div className={`relative h-5 w-9 flex-shrink-0 rounded-full transition-colors duration-200 ${rememberMe ? "bg-blue-500" : "bg-slate-200 dark:bg-slate-700"}`}>
                    <div className={`absolute top-0.5 h-4 w-4 rounded-full bg-white dark:bg-slate-300 shadow-sm transition-transform duration-200 ${rememberMe ? "translate-x-[18px]" : "translate-x-0.5"}`} />
                  </div>
                  <span className="text-[13px] text-slate-500 select-none group-hover:text-slate-700 transition-colors">Запомнить</span>
                </button>
                <Link to="/reset-password" className="text-[13px] font-semibold text-blue-500 hover:text-blue-700 transition-colors">
                  Забыли пароль?
                </Link>
              </div>
            </SlideSection>
          </div>

          <div role="alert" aria-live="polite">
            {err && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-red-50/80 border border-red-100">
                <svg className="h-4 w-4 text-red-400 flex-shrink-0" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-red-600 text-[13px] font-semibold">{err}</span>
              </div>
            )}
          </div>

          <div className="pt-2 space-y-4">
            <SubmitButton busy={busy}>{isRegister ? "Создать аккаунт" : "Войти"}</SubmitButton>
            <p key={isRegister ? "to-login" : "to-reg"} className="mode-in text-[13px] text-center text-slate-500">
              {isRegister ? (
                <>
                  Уже есть аккаунт?{" "}
                  <button type="button" onClick={() => { setMode("login"); setSearchParams({}); }} className="font-bold text-blue-600 hover:text-blue-800 transition-colors">
                    Войти
                  </button>
                </>
              ) : (
                <>
                  Нет аккаунта?{" "}
                  <button type="button" onClick={() => { setMode("register"); setSearchParams({ mode: "register" }); }} className="font-bold text-blue-600 hover:text-blue-800 transition-colors">
                    Зарегистрируйтесь
                  </button>
                </>
              )}
            </p>
          </div>
        </form>
      </Card>
    </div>
  );
}
